import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {ProductCardData} from '../../scratchpad/productCardScratchpad.types';
import {sharedCampaigns} from '@cl/shared';
import relatedItemsApi from 'common/src/modules/relatedItems/relatedItems.api';
import {DTProductSelectionNode} from 'common/src/modules/decisionTree/decisionTree.types';
import {CLStore} from 'common/src/redux/store';
import getStrategies, {FeedType} from '../../../../utils/getStrategies';
import productFeedApi from 'common/src/modules/productFeed/productFeed.api';

function createDefaultProductCard(): ProductCardData {
  return {
    id: NO_NODE_ID,
    name: 'Product Card',
    description: '',
    type: NodeType.PRODUCTS,
    outputs: [
      {
        id: 1,
        name: 'Fallback',
        type: OutputType.Fallback,
        nodeId: NO_NODE_ID,
      },
    ],
    inputs: [],
    nextOutputId: 1,
    availableStrategies: [],
    feedId: 0,
    productsSelectionStrategy: 0,
    topItemLogicId: 0,
    retargetingProductSetId: 0,
    categoryTopItemsLogicId: 0,
  };
}

/**
 * Gets an existing product card by ID, or creates a new default one.
 * @param cardId - The card ID to fetch, or 0/undefined for a new card
 * @returns The product card (existing or new), fully mutable
 */
async function getOrCreateProductCard(
  cardId: number | undefined,
  store: CLStore,
): Promise<ProductCardData> {
  const resolvedCardId = cardId && cardId > NO_NODE_ID ? cardId : NO_NODE_ID;

  if (resolvedCardId) {
    const existingCard = sharedCampaigns.nodesRef.value[resolvedCardId];

    if (!existingCard) {
      throw new Error(`Card with id ${resolvedCardId} not found`);
    }

    if (existingCard.type !== NodeType.PRODUCTS) {
      throw new Error(`Card with id ${resolvedCardId} is not a product card`);
    }

    const campaign = store.getState().campaignPage.campaignTree.campaign;

    if (!campaign) {
      throw new Error('Campaign not found');
    }

    const campaignType = campaign.type;

    const productSelectionNode = existingCard as DTProductSelectionNode;

    const relatedItemsLogics = await relatedItemsApi.getRelatedItemsLogicsForFeed({
      feedId: productSelectionNode.feedId,
    });

    const availableStrategies = getStrategies(FeedType.Product, campaignType);

    const availableFeeds = await productFeedApi.getStrategyOptionalFeeds(
      productSelectionNode.productsSelectionStrategy,
    );

    return {
      ...productSelectionNode,
      relatedItemsLogics,
      availableStrategies,
      availableFeeds,
    } as ProductCardData;
  }

  return createDefaultProductCard();
}

export default getOrCreateProductCard;
